import React, { useState, useEffect, useCallback } from 'react';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_calendar.css';
import roleApi from '@services/roleApi';
import branchApi from '@services/branchApi';
import Pagination from '@components/common/Pagination';
import AlertModal from '@components/common/AlertModal';
import ConfirmModal from '@components/common/ConfirmModal';

const AccountGroupManagement = () => {
  // 상태 관리
  const [groups, setGroups] = useState([]);
  const [branches, setBranches] = useState([]); // 지점 목록
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 페이지네이션 관련 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // 체크박스 상태 관리
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);
  
  // 현재 선택된 그룹
  const [currentGroup, setCurrentGroup] = useState(null);
  
  // 모달 입력값 상태 관리
  const [groupModalInputs, setGroupModalInputs] = useState({
    branch: '', // 지점 선택
    roleType: '' // 그룹타입 선택
    // CRUD 권한 및 Visibility - 추후 구현 예정
    // readPermission: false,
    // writePermission: false,
    // updatePermission: false,
    // deletePermission: false,
    // visibility: false
  });
  
  // 권한 관련 상태
  const [availablePermissions, setAvailablePermissions] = useState([
    { id: 'permission-1', name: '권한관리' },
    { id: 'permission-2', name: '데이터관리' },
    { id: 'permission-3', name: '기기관리' },
    { id: 'permission-4', name: '사용자 메뉴 관리' },
    { id: 'permission-5', name: '기기세팅' },
    { id: 'permission-6', name: 'QR 서버 관리' },
    { id: 'permission-7', name: '매출 관리' },
    { id: 'permission-8', name: '매뉴얼 페이지' }
  ]);
  
  const [selectedPermissions, setSelectedPermissions] = useState([
    { id: 'permission-default', name: '소프트웨어 관리' }
  ]);
  
  const [selectedAvailablePermission, setSelectedAvailablePermission] = useState(null);
  const [selectedAddedPermission, setSelectedAddedPermission] = useState(null);
  
  // 다중 선택을 위한 상태 추가
  const [selectedAvailablePermissions, setSelectedAvailablePermissions] = useState([]);
  const [selectedAddedPermissions, setSelectedAddedPermissions] = useState([]);
  
  // 필터 상태
  const [filterType, setFilterType] = useState('latest');
  
  // Spring Data의 sort 파라미터로 변환
  const getSortParam = (filterType) => {
    switch (filterType) {
      case 'name':
        return 'name,asc';
      case 'period':
        return 'createdAt,asc'; // 사용기간순 (오래된 순)
      case 'latest':
      default:
        return 'createdAt,desc'; // 최신순
    }
  };
  
  // 권한 그룹 목록 조회 함수
  const fetchRoleGroups = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Spring Data는 0부터 페이지 번호 시작
      const backendPage = currentPage - 1;
      const sortParam = getSortParam(filterType);

      // 실제 API 호출
      const response = await roleApi.getRoleGroups(backendPage, pageSize, sortParam);

      // 응답 데이터 처리
      if (response && response.success && response.data) {
        const { content, totalElements, totalPages } = response.data;

        // Role 데이터를 Group 형식으로 매핑 (기본 지점 설정)
        const mappedGroups = content.map(role => ({
          ...role,
          branch: role.branch || '', // 지점 정보
          permissions: role.permissions || [] // 권한 목록
        }));

        setGroups(mappedGroups);
        setTotalItems(totalElements || 0);
        setTotalPages(totalPages || 1);
      } else {
        // 응답 형식이 다르면 빈 데이터 설정
        setGroups([]);
        setTotalItems(0);
        setTotalPages(1);
      }

    } catch (err) {
      console.error('권한 그룹 목록 조회 실패:', err);
      setError('권한 그룹 목록을 불러오는 중 오류가 발생했습니다.');
      setGroups([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterType]);
  
  // 지점 목록 가져오기
  const fetchBranches = useCallback(async () => {
    try {
      const response = await branchApi.getBranches({
        page: 0,
        size: 100, // 충분한 수의 지점을 가져오기
        sort: 'name,asc'
      });

      if (response && response.success && response.data) {
        // 페이징 응답인 경우 content 추출
        const branchData = response.data.content || response.data;

        // 활성 상태인 지점만 필터링
        const activeBranches = Array.isArray(branchData)
          ? branchData.filter(branch => branch.status === 'ACTIVE')
          : [];

        if (activeBranches.length > 0) {
          setBranches(activeBranches);
        } else {
          console.warn('활성 지점이 없습니다.');
          setBranches([]);
        }
      } else if (response && response.data) {
        // 다른 응답 형식 처리
        const branchData = response.data.content || response.data;
        setBranches(Array.isArray(branchData) ? branchData : []);
      } else {
        console.warn('예상치 못한 API 응답 형식:', response);
        setBranches([]);
      }
    } catch (error) {
      console.error('지점 목록 조회 실패:', error);
      // API 실패시 빈 배열로 설정 (하드코딩 제거)
      setBranches([]);

      // 사용자에게 에러 알림
      setAlertMessage('지점 목록을 불러오는데 실패했습니다. 지점 관리에서 지점을 먼저 등록해주세요.');
      setShowAlertModal(true);
    }
  }, []);

  // 초기 데이터 로드
  useEffect(() => {
    fetchRoleGroups();
    fetchBranches(); // 지점 목록도 함께 로드
  }, [fetchRoleGroups, fetchBranches]);
  
  // 페이지 변경 핸들러
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // 체크박스 전체 선택/해제 핸들러
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    setSelectedItems(e.target.checked ? groups.map(group => group.id) : []);
  };
  
  // 개별 체크박스 핸들러
  const handleItemSelect = (id, e) => {
    if (e.target.checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(item => item !== id));
    }
    
    // 전체 선택 상태 업데이트
    if (e.target.checked && selectedItems.length + 1 === groups.length) {
      setSelectAll(true);
    } else if (!e.target.checked && selectAll) {
      setSelectAll(false);
    }
  };
  
  // 필터 변경 핸들러
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
    setCurrentPage(1); // 필터 변경 시 첫 페이지로 이동
  };
  
  // 그룹 추가 모달 열기 핸들러
  const handleAddGroupClick = () => {
    setGroupModalInputs({
      branch: '',
      roleType: ''
      // CRUD 권한 및 Visibility - 추후 구현 예정
      // readPermission: false,
      // writePermission: false,
      // updatePermission: false,
      // deletePermission: false,
      // visibility: false
    });
    setSelectedPermissions([
      { id: 'permission-default', name: '소프트웨어 관리' }
    ]);
    setShowAddModal(true);
  };
  
  // 그룹 수정 모달 열기 핸들러
  const handleEditClick = (group) => {
    setCurrentGroup(group);
    setGroupModalInputs({
      branch: group.branch,
      roleType: group.roleType || 'ROLE_USER'
      // CRUD 권한 및 Visibility - 추후 구현 예정
      // readPermission: group.readPermission || false,
      // writePermission: group.writePermission || false,
      // updatePermission: group.updatePermission || false,
      // deletePermission: group.deletePermission || false,
      // visibility: group.visibility || false
    });
    
    // 선택된 권한 설정
    const selected = [
      { id: 'permission-default', name: '소프트웨어 관리' },
      ...group.permissions.map((perm, index) => {
        // 일치하는 권한 ID 찾기
        const foundPermission = availablePermissions.find(p => p.name === perm);
        return foundPermission || { id: `permission-custom-${index}`, name: perm };
      })
    ];
    
    setSelectedPermissions(selected);
    setShowEditModal(true);
  };
  
  // 그룹 삭제 버튼 핸들러
  const handleDeleteClick = (group) => {
    setCurrentGroup(group);
    setConfirmMessage(`${group.name} 그룹을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        // API를 통한 그룹 삭제
        const response = await roleApi.deleteRoleGroup(group.id);

        if (response && response.success) {
          // 성공 시 목록 새로고침
          await fetchRoleGroups();
          setAlertMessage('삭제되었습니다.');
        } else {
          setAlertMessage(response?.message || '삭제에 실패했습니다.');
        }
      } catch (error) {
        console.error('그룹 삭제 오류:', error);
        setAlertMessage('삭제 중 오류가 발생했습니다.');
      }
      setShowAlertModal(true);
    });
    setShowConfirmModal(true);
  };
  
  // 모달 입력값 변경 핸들러
  const handleGroupInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGroupModalInputs({
      ...groupModalInputs,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  
  // 권한 선택 핸들러
  const handlePermissionSelect = (type, id) => {
    if (type === 'available') {
      setSelectedAvailablePermission(id);
      setSelectedAddedPermission(null);
    } else {
      setSelectedAddedPermission(id);
      setSelectedAvailablePermission(null);
    }
  };
  
  // 다중 선택 핸들러
  const handlePermissionCheckboxChange = (type, permissionId) => {
    if (type === 'available') {
      setSelectedAvailablePermissions(prev => {
        if (prev.includes(permissionId)) {
          return prev.filter(id => id !== permissionId);
        } else {
          return [...prev, permissionId];
        }
      });
    } else {
      setSelectedAddedPermissions(prev => {
        if (prev.includes(permissionId)) {
          return prev.filter(id => id !== permissionId);
        } else {
          return [...prev, permissionId];
        }
      });
    }
  };
  
  // 다중 추가 핸들러
  const handleMultiAddPermission = () => {
    if (selectedAvailablePermissions.length === 0) return;
    
    const permissionsToAdd = availablePermissions.filter(p => 
      selectedAvailablePermissions.includes(p.id) && 
      !selectedPermissions.some(sp => sp.id === p.id)
    );
    
    if (permissionsToAdd.length > 0) {
      setSelectedPermissions([...selectedPermissions, ...permissionsToAdd]);
      setSelectedAvailablePermissions([]);
    }
  };
  
  // 다중 제거 핸들러
  const handleMultiRemovePermission = () => {
    if (selectedAddedPermissions.length === 0) return;
    
    setSelectedPermissions(selectedPermissions.filter(p => !selectedAddedPermissions.includes(p.id)));
    setSelectedAddedPermissions([]);
  };
  
  // 권한 추가 핸들러
  const handleAddPermission = () => {
    if (!selectedAvailablePermission) return;
    
    const permissionToAdd = availablePermissions.find(p => p.id === selectedAvailablePermission);
    if (!permissionToAdd) return;
    
    // 이미 추가된 권한인지 확인
    const isDuplicate = selectedPermissions.some(p => p.id === permissionToAdd.id);
    if (isDuplicate) return;
    
    setSelectedPermissions([...selectedPermissions, permissionToAdd]);
    setSelectedAvailablePermission(null);
  };
  
  // 권한 제거 핸들러
  const handleRemovePermission = () => {
    if (!selectedAddedPermission || selectedAddedPermission === 'permission-default') return;
    
    setSelectedPermissions(selectedPermissions.filter(p => p.id !== selectedAddedPermission));
    setSelectedAddedPermission(null);
  };
  
  // 모달 제출 핸들러 (추가)
  const handleAddGroupSubmit = async () => {
    const { roleType, branch } = groupModalInputs;

    // 유효성 검사
    if (!roleType) {
      setAlertMessage('그룹타입을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }

    if (roleType !== 'ROLE_ADMIN' && !branch) {
      setAlertMessage('지점을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }

    try {
      // 선택된 지점의 ID 찾기
      const selectedBranch = branches.find(b => b.name === branch);
      const branchId = selectedBranch ? selectedBranch.id : null;

      // 역할 타입에 대한 그룹명 설정 (roleType을 그대로 name으로 사용)
      const groupName = roleType;

      // API를 통한 그룹 생성
      const groupData = {
        groupName: groupName,
        name: groupName,
        branch: branch,
        branchId: branchId,
        roleType: roleType,
        permissionIds: selectedPermissions
          .filter(p => p.id !== 'permission-default')
          .map(p => p.id),  // id를 전달
        description: `${roleType} 권한 그룹`
      };

      const response = await roleApi.createRoleGroup(groupData);

      if (response && response.success) {
        // 성공 시 목록 새로고침
        await fetchRoleGroups();
        setShowAddModal(false);
        setAlertMessage('그룹이 추가되었습니다.');
      } else {
        setAlertMessage(response?.message || '그룹 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('그룹 추가 오류:', error);
      // 백엔드 에러 응답에서 메시지 추출
      if (error.response && error.response.data && error.response.data.message) {
        setAlertMessage(error.response.data.message);
      } else {
        setAlertMessage('그룹 추가 중 오류가 발생했습니다.');
      }
    }

    setShowAlertModal(true);
  };
  
  // 모달 제출 핸들러 (수정)
  const handleEditGroupSubmit = async () => {
    const { roleType, branch } = groupModalInputs;

    // 유효성 검사
    if (!roleType) {
      setAlertMessage('그룹타입을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }

    if (roleType !== 'ROLE_ADMIN' && !branch) {
      setAlertMessage('지점을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    try {
      // 선택된 지점의 ID 찾기
      const selectedBranch = branches.find(b => b.name === branch);
      const branchId = selectedBranch ? selectedBranch.id : null;

      // 역할 타입에 대한 그룹명 설정 (roleType을 그대로 name으로 사용)
      const groupName = roleType;

      // API를 통한 그룹 수정
      const groupData = {
        groupName: groupName,
        name: groupName,
        branch: branch,
        branchId: branchId,
        roleType: roleType,
        permissionIds: selectedPermissions
          .filter(p => p.id !== 'permission-default')
          .map(p => p.id),  // id를 전달
        description: `${roleType} 권한 그룹`
      };

      const response = await roleApi.updateRoleGroup(currentGroup.id, groupData);

      if (response && response.success) {
        // 성공 시 목록 새로고침
        await fetchRoleGroups();
        setShowEditModal(false);

        const nameChanged = currentGroup.name !== groupName;
        const message = nameChanged
          ? `그룹명이 '${currentGroup.name}'에서 '${groupName}'으로 변경되었습니다.`
          : `${groupName} 그룹의 권한이 수정되었습니다.`;

        setAlertMessage(`${groupName} 그룹이 수정되었습니다.`);
      } else {
        setAlertMessage(response?.message || '그룹 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('그룹 수정 오류:', error);
      // 백엔드 에러 응답에서 메시지 추출
      if (error.response && error.response.data && error.response.data.message) {
        setAlertMessage(error.response.data.message);
      } else {
        setAlertMessage('그룹 수정 중 오류가 발생했습니다.');
      }
    }

    setShowAlertModal(true);
  };
  
  // 모달 닫기 함수
  const handleCloseAllModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDetailModal(false);
    setShowAlertModal(false);
    setShowConfirmModal(false);
  };
  
  // 알림 모달만 닫는 함수
  const handleCloseAlertModal = () => {
    setShowAlertModal(false);
  };
  
  // 상세정보 모달만 닫는 함수
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
  };
  
  // 확인 모달 확인 버튼 핸들러
  const handleConfirmModalConfirm = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirmModal(false);
  };
  
  // 그룹 상세정보 보기 핸들러
  const handleInfoClick = (group) => {
    setCurrentGroup(group);
    setShowDetailModal(true);
  };
  
  // 그룹 모달 렌더링 함수
  const renderGroupModal = (isEdit) => {
    return (
      <div className="modal-overlay active" id={isEdit ? "edit-group-modal" : "create-theme-modal"}>
        <div className="modal-container">
          <div className="modal-header">
            <h3 className="modal-title">{isEdit ? "계정권한 그룹 수정" : "계정권한 그룹 추가"}</h3>
            <button className="modal-close" onClick={handleCloseAllModals}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="modal-form-group">
              <label className="modal-label">그룹타입</label>
              <div className="modal-select-container">
                <select
                  className="modal-select"
                  name="roleType"
                  value={groupModalInputs.roleType}
                  onChange={handleGroupInputChange}
                >
                  <option value="">그룹타입 선택</option>
                  <option value="ROLE_ADMIN">ROLE_ADMIN (시스템 관리자)</option>
                  <option value="ROLE_MANAGER">ROLE_MANAGER (지점 관리자)</option>
                  <option value="ROLE_USER">ROLE_USER (일반 사용자)</option>
                </select>
                <span className="ri-arrow-down-s-line modal-select-arrow"></span>
              </div>
            </div>

            {/* ADMIN이 아닌 경우에만 지점 선택 표시 */}
            {groupModalInputs.roleType && groupModalInputs.roleType !== 'ROLE_ADMIN' && (
              <div className="modal-form-group">
                <label className="modal-label">지점</label>
                <div className="modal-select-container">
                  <select
                    className="modal-select"
                    name="branch"
                    value={groupModalInputs.branch}
                    onChange={handleGroupInputChange}
                  >
                    <option value="">지점 선택</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.name}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <span className="ri-arrow-down-s-line modal-select-arrow"></span>
                </div>
              </div>
            )}
            
            {/* CRUD 권한 및 Visibility 필드 - 추후 구현 예정으로 숨김 처리 */}
            {/* <div className="modal-form-group toggle-group">
              <div className="toggle-label">읽기 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="read-permission"
                    name="readPermission"
                    checked={groupModalInputs.readPermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="modal-form-group toggle-group">
              <div className="toggle-label">쓰기 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="write-permission"
                    name="writePermission"
                    checked={groupModalInputs.writePermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="modal-form-group toggle-group">
              <div className="toggle-label">수정 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="update-permission"
                    name="updatePermission"
                    checked={groupModalInputs.updatePermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="modal-form-group toggle-group">
              <div className="toggle-label">삭제 권한</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="delete-permission"
                    name="deletePermission"
                    checked={groupModalInputs.deletePermission}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="modal-form-group toggle-group">
              <div className="toggle-label">Visibility</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="visibility"
                    name="visibility"
                    checked={groupModalInputs.visibility}
                    onChange={handleGroupInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div> */}

            {/* 권한 설정 - 추후 구현 예정
            <div className="modal-form-group">
              <label className="modal-label">권한 설정</label>
              <div className="permission-transfer-container">
                <div className="permission-box">
                  <div className="permission-header">접근 권한 목록</div>
                  <div className="permission-list" id="available-permissions">
                    {availablePermissions.map(permission => (
                      <div
                        key={`${permission.id}${isEdit ? '-edit' : ''}`}
                        className={`permission-item ${selectedAvailablePermissions.includes(permission.id) ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          id={`${permission.id}${isEdit ? '-edit' : ''}`}
                          name={`permission-select${isEdit ? '-edit' : ''}`}
                          className="permission-checkbox"
                          checked={selectedAvailablePermissions.includes(permission.id)}
                          onChange={() => handlePermissionCheckboxChange('available', permission.id)}
                        />
                        <label htmlFor={`${permission.id}${isEdit ? '-edit' : ''}`}>{permission.name}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="permission-controls">
                  <button
                    id="add-permission-btn"
                    className="permission-control-btn"
                    onClick={handleMultiAddPermission}
                  >
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <button
                    id="remove-permission-btn"
                    className="permission-control-btn"
                    onClick={handleMultiRemovePermission}
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                </div>

                <div className="permission-box">
                  <div className="permission-header">추가된 권한</div>
                  <div className="permission-list" id="selected-permissions">
                    {selectedPermissions.map(permission => (
                      <div
                        key={`${permission.id}-selected${isEdit ? '-edit' : ''}`}
                        className={`permission-item ${selectedAddedPermissions.includes(permission.id) ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          id={`${permission.id}-selected${isEdit ? '-edit' : ''}`}
                          name={`selected-permission${isEdit ? '-edit' : ''}`}
                          className="permission-checkbox"
                          checked={selectedAddedPermissions.includes(permission.id)}
                          onChange={() => handlePermissionCheckboxChange('selected', permission.id)}
                        />
                        <label htmlFor={`${permission.id}-selected${isEdit ? '-edit' : ''}`}>{permission.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            */}
          </div>
          <div className="modal-footer">
            <button 
              className="modal-btn modal-btn-cancel" 
              onClick={handleCloseAllModals}
            >
              취소
            </button>
            <button 
              className="modal-btn modal-btn-primary" 
              onClick={isEdit ? handleEditGroupSubmit : handleAddGroupSubmit}
            >
              확인
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // 일괄 삭제 핸들러
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return;

    setConfirmMessage(`선택한 ${selectedItems.length}개의 그룹을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      let successCount = 0;
      let failCount = 0;

      // 각 아이템을 순차적으로 삭제
      for (const id of selectedItems) {
        try {
          const response = await roleApi.deleteRoleGroup(id);
          if (response && response.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`그룹 ${id} 삭제 실패:`, error);
          failCount++;
        }
      }

      // 목록 새로고침
      await fetchRoleGroups();
      setSelectedItems([]);
      setSelectAll(false);

      // 결과 메시지
      if (failCount === 0) {
        setAlertMessage(`${successCount}개의 그룹이 삭제되었습니다.`);
      } else {
        setAlertMessage(`${successCount}개 삭제 성공, ${failCount}개 삭제 실패`);
      }
      setShowAlertModal(true);
    });
    setShowConfirmModal(true);
  };

  return (
    <>
      <div className="page-title-container">
        <h1 className="page-title">계정권한 그룹</h1>
        <div className="page-title-icon">
          <a href="#" id="add-theme-btn" title="계정권한 그룹 추가" onClick={handleAddGroupClick}>
            <i className="ri-add-circle-line"></i>
          </a>
        </div>
      </div>
      
      <div className="page-container">
        {/* 필터 드롭다운 */}
        <div className="filter-dropdown-container-row">
          {/* 일괄 삭제 버튼 */}
          <div className={`bulk-delete-container ${selectedItems.length >= 2 ? 'active' : ''}`}>
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              일괄 삭제 ({selectedItems.length})
            </button>
          </div>

          <div className="filter-dropdown">
            <select 
              id="group-filter"
              value={filterType}
              onChange={handleFilterChange}
            >
              <option value="latest">최신순</option>
              <option value="name">이름순</option>
              <option value="period">사용기간순</option>
            </select>
          </div>
        </div>
        
        {/* 그룹 테이블 */}
        <div className="common-table-container">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>데이터를 불러오는 중입니다...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button 
                className="retry-button"
                onClick={fetchRoleGroups}
              >
                다시 시도
              </button>
            </div>
          ) : (
            <table className="common-table">
              <thead>
                <tr>
                  <th>
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="select-all" className="custom-checkbox"></label>
                    </div>
                  </th>
                  <th>NO</th>
                  <th>역할 타입</th>
                  <th>지점</th>
                  <th>그룹</th>
                  <th>상세정보</th>
                  <th>수정</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data-message">
                      등록된 계정권한 그룹이 없습니다.
                    </td>
                  </tr>
                ) : (
                  groups.map((group, index) => (
                    <tr key={group.id}>
                      <td>
                        <div className="checkbox-container">
                          <input 
                            type="checkbox" 
                            id={`group-${group.id}`}
                            checked={selectedItems.includes(group.id)}
                            onChange={(e) => handleItemSelect(group.id, e)}
                          />
                          <label htmlFor={`group-${group.id}`} className="custom-checkbox"></label>
                        </div>
                      </td>
                      <td>{index + 1}</td>
                      <td>
                        {group.roleType === 'ROLE_ADMIN' && '시스템 관리자'}
                        {group.roleType === 'ROLE_MANAGER' && '지점 관리자'}
                        {(!group.roleType || group.roleType === 'ROLE_USER') && '일반 사용자'}
                      </td>
                      <td>{group.roleType === 'ROLE_ADMIN' ? '전체' : (group.branch || '-')}</td>
                      <td>{group.name}</td>
                      <td>
                        <button 
                          className="info-btn"
                          onClick={() => handleInfoClick(group)}
                        >
                          <i className="ri-information-line"></i>
                        </button>
                      </td>
                      <td>
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditClick(group)}
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                      </td>
                      <td>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteClick(group)}
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* 페이지네이션 */}
        {!loading && !error && totalPages > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageGroupSize={5}
          />
        )}
      </div>
      
      {/* 그룹 추가 모달 */}
      {showAddModal && renderGroupModal(false)}
      
      {/* 그룹 수정 모달 */}
      {showEditModal && currentGroup && renderGroupModal(true)}
      
      {/* 그룹 상세정보 모달 */}
      {showDetailModal && currentGroup && (
        <div className="modal-overlay active" id="group-detail-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">계정권한 그룹 상세정보</h3>
              <button className="modal-close" onClick={handleCloseDetailModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-item-container">
                <span className="detail-label">역할 타입</span>
                <span className="detail-content">
                  {currentGroup.roleType === 'ROLE_ADMIN' && '시스템 관리자'}
                  {currentGroup.roleType === 'ROLE_MANAGER' && '지점 관리자'}
                  {(!currentGroup.roleType || currentGroup.roleType === 'ROLE_USER') && '일반 사용자'}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">지점</span>
                <span className="detail-content">
                  {currentGroup.roleType === 'ROLE_ADMIN' ? '전체' : (currentGroup.branch || '-')}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">그룹명</span>
                <span className="detail-content">{currentGroup.name}</span>
              </div>
              {/* CRUD 권한 및 Visibility - 추후 구현 예정으로 숨김 처리 */}
              {/* <div className="detail-item-container">
                <span className="detail-label">권한</span>
                <span className="detail-content">
                  {[
                    currentGroup.readPermission ? '읽기' : null,
                    currentGroup.writePermission ? '쓰기' : null,
                    currentGroup.updatePermission ? '수정' : null,
                    currentGroup.deletePermission ? '삭제' : null
                  ].filter(Boolean).join(', ') || '없음'}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">Visibility</span>
                <span className="detail-content">
                  {currentGroup.visibility ? 'Y' : 'N'}
                </span>
              </div> */}
              <div className="detail-item-container">
                <span className="detail-label">접근 권한</span>
                <span className="detail-content">
                  {currentGroup.permissions && currentGroup.permissions.length > 0
                    ? currentGroup.permissions.join(', ')
                    : '없음'}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">생성일</span>
                <span className="detail-content">
                  {currentGroup.createdAt
                    ? new Date(currentGroup.createdAt).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      }).replace(/\. /g, '-').replace(/\.$/, '')
                    : '-'}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-primary" 
                onClick={handleCloseDetailModal}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 알림/확인 모달 */}
      <AlertModal
        isOpen={showAlertModal}
        message={alertMessage}
        onClose={handleCloseAlertModal}
      />
      
      <ConfirmModal
        isOpen={showConfirmModal}
        message={confirmMessage}
        onConfirm={handleConfirmModalConfirm}
        onCancel={handleCloseAllModals}
      />
    </>
  );
};

export default AccountGroupManagement;